import { X } from "lucide-react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";
import { Separator } from "@/common/ui/separator";

import type { ConversationLogEntry } from "./conversation-logs-types";

interface LogDetailPanelProps {
  entry: ConversationLogEntry;
  onClose: () => void;
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

export function LogDetailPanel({ entry, onClose }: LogDetailPanelProps): React.ReactElement {
  return (
    <Card data-testid="log-detail-panel" className="h-fit">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">Event Details</CardTitle>
        <Button
          data-testid="close-detail-button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <DetailField label="Timestamp" value={new Date(entry.timestamp).toLocaleString()} />
          <DetailField label="Action" value={entry.action} />
          <DetailField label="User ID" value={<span className="font-mono">{entry.user_id}</span>} />
          <DetailField
            label="Session ID"
            value={<span className="font-mono text-xs">{entry.session_id}</span>}
          />
          <DetailField label="Model" value={entry.model_id} />
          <DetailField label="Environment" value={entry.environment} />
          {entry.model_version && <DetailField label="Model Version" value={entry.model_version} />}
          {entry.token_id && (
            <DetailField
              label="Token ID"
              value={<span className="font-mono text-xs">{entry.token_id}</span>}
            />
          )}
        </div>

        <Separator />

        <DetailField
          label="Permissions"
          value={
            <div className="flex flex-wrap gap-1 pt-0.5">
              {entry.permissions.map((p) => (
                <Badge key={p} variant="outline" className="text-xs">
                  {p}
                </Badge>
              ))}
            </div>
          }
        />

        <DetailField
          label="Tenant Scope"
          value={
            <div className="space-y-0.5 text-xs">
              <div>Enterprise: {entry.tenant_scope.enterprise_id ?? "N/A"}</div>
              <div>Org Unit: {entry.tenant_scope.org_unit_id ?? "N/A"}</div>
            </div>
          }
        />

        {entry.sources_cited.length > 0 && (
          <>
            <Separator />
            <DetailField
              label="Sources Cited"
              value={
                <ul data-testid="sources-list" className="list-inside list-disc text-xs">
                  {entry.sources_cited.map((src) => (
                    <li key={src}>{src}</li>
                  ))}
                </ul>
              }
            />
          </>
        )}

        {entry.tool_calls && entry.tool_calls.length > 0 && (
          <>
            <Separator />
            <div>
              <span className="text-xs font-medium text-muted-foreground">Tool Calls</span>
              <div data-testid="tool-calls-list" className="mt-1 space-y-2">
                {entry.tool_calls.map((tc, i) => (
                  <div key={`${tc.tool_name}-${i}`} className="rounded border p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-medium">{tc.tool_name}</span>
                      <Badge
                        variant={tc.auth_result === "authorized" ? "default" : "destructive"}
                        className="text-xs"
                      >
                        {tc.auth_result}
                      </Badge>
                    </div>
                    <pre className="mt-1 overflow-x-auto text-muted-foreground">
                      {JSON.stringify(tc.parameters, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {entry.guardrail_intervention && (
          <>
            <Separator />
            <div>
              <span className="text-xs font-medium text-muted-foreground">
                Guardrail Intervention
              </span>
              <div
                data-testid="guardrail-detail"
                className="mt-1 rounded border border-destructive/30 bg-destructive/5 p-2 text-xs"
              >
                <div className="flex items-center gap-2">
                  <Badge variant="destructive">{entry.guardrail_intervention.action}</Badge>
                  <span className="text-muted-foreground">
                    Source: {entry.guardrail_intervention.source}
                  </span>
                </div>
                {entry.guardrail_intervention.reason && (
                  <p className="mt-1">{entry.guardrail_intervention.reason}</p>
                )}
                <div className="mt-1 flex flex-wrap gap-1">
                  {entry.guardrail_intervention.policies.map((p) => (
                    <Badge key={p} variant="outline" className="text-xs">
                      {p}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {entry.prefills && entry.prefills.length > 0 && (
          <>
            <Separator />
            <div>
              <span className="text-xs font-medium text-muted-foreground">Prefills</span>
              <div data-testid="prefills-list" className="mt-1 space-y-2">
                {entry.prefills.map((pf) => (
                  <div key={pf.form_id} className="rounded border p-2 text-xs">
                    <div>Form: {pf.form_id}</div>
                    <div>
                      Entries: {pf.entry_count} | Gaps: {pf.gap_count}
                    </div>
                    <a
                      href={pf.deep_link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      Open Form
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
